import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { debounceTime, delay, Subject, Subscription } from 'rxjs';

import { PatientService } from '../../../shared/services/patient.service';
import { PatientInfo } from '../../../shared/models/patient.model';
import { SchemaService } from '../../../shared/services/schema.service';
import { SchemaType } from '../../../shared/models/schema.model';
import { RequisitionService } from '../../../shared/services/requisition.service';
import { SessionStoreRepository } from '../../../shared/repositories/session.repository';
import { SchemaRepository } from '../../../shared/repositories/schema.repository';

@Component({
    selector: 'new-schema-dialog',
    imports: [CommonModule, TranslocoDirective, ReactiveFormsModule],
    templateUrl: './new-schema-dialog.component.html',
    styleUrl: './new-schema-dialog.component.scss'
})
export class NewSchemaDialogComponent implements OnInit, OnDestroy {
    private readonly patientService = inject(PatientService);
    private readonly schemaService = inject(SchemaService);
    private readonly schemaRepository = inject(SchemaRepository);
    private readonly requisitionService = inject(RequisitionService);
    private readonly sessionRepository = inject(SessionStoreRepository)
    private readonly fb = inject(FormBuilder);

    private subscription = new Subscription();
    private fetchPatientDebounce$ = new Subject<void>();

    private modalElement!: HTMLElement;
    private clinicInfo = this.sessionRepository.userInfo;
    private ssn: string = "";

    requisitionForm!: FormGroup;
    schemaTypes: SchemaType[] = [];

    @Output() newRequisitionEvent = new EventEmitter<void>();

    requisitionIsSent: WritableSignal<boolean | undefined> = signal(false);
    patientExists: WritableSignal<boolean | undefined> = signal(false);
    isLoading: WritableSignal<boolean> = signal(false);
    requisitionSendError: WritableSignal<boolean> = signal(false);


    ngOnInit(): void {
        this.schemaService.getSchemaTypes$().subscribe({
            next: (data) => {
                this.schemaTypes = data.map((x: SchemaType) => ({
                    schemaGuid: x.schemaGuid,
                    name: x.name
                }));
            },
        })

        this.initFormInputs();

        this.fetchPatientDebounce$.pipe(
            debounceTime(500)
        ).subscribe(() => this.fetchPatientData());
    }

    ngAfterViewInit(): void {
        this.modalElement = document.getElementById('newSchemaModal')!;
        this.modalElement!.addEventListener('hidden.bs.modal', () => {
            this.onModalHidden();
        });
    }

    private initFormInputs() {
        this.requisitionForm = this.fb.group({
            cprNumber: [''],
            firstName: [''],
            lastName: [''],
            sms: [''],
            email: [''],
            schemaGuid: [''],
            validFrom: [''],
            validTo: [''],
            notifySender: [true],
            notifyPatient: [true],
            providerNumber: [this.clinicInfo?.providerNumber],
            organisation: [this.clinicInfo?.firstName],
            initials: [''],
        });
    }

    private onModalHidden = () => {
        this.requisitionIsSent.set(false);
    };

        private resetForm() {
        if (this.requisitionIsSent() === true) {
            this.requisitionIsSent.set(undefined);
            this.patientExists.set(undefined);
        }
        this.requisitionSendError.set(false);
        this.requisitionForm.reset();
        this.requisitionForm.patchValue({
            providerNumber: this.clinicInfo?.providerNumber,
            organisation: this.clinicInfo?.firstName,
            notifySender: true,
            notifyPatient: true,
        });
    }

    fetchPatientData(): void {
        this.isLoading.set(true);
        this.requisitionForm.disable();
        this.ssn = this.requisitionForm.get("cprNumber")?.value;
        const preservedSsn = this.ssn;
        const patchedValues = {
            providerNumber: this.clinicInfo?.providerNumber,
            organisation: this.clinicInfo?.firstName,
            notifySender: true,
            notifyPatient: true,
            cprNumber: preservedSsn
        };

        if (this.ssn) {
            this.subscription.add(
                this.patientService.postFindPatientByCpr$(this.ssn).pipe(delay(500))
                    .subscribe({
                        next: (data: PatientInfo) => {
                            if (!data) {
                                this.patientExists.set(false);
                                this.requisitionForm.reset();
                                this.requisitionForm.patchValue(patchedValues)
                            } else {
                                this.patientExists.set(true);
                                this.requisitionForm.patchValue({
                                    firstName: data.firstName,
                                    lastName: data.surname,
                                    email: data.email
                                })
                            }
                        },
                        error: () => {
                            this.patientExists.set(false);
                            this.isLoading.set(false);
                            this.requisitionForm.enable();
                            this.requisitionForm.reset();
                            this.requisitionForm.patchValue(patchedValues)
                        },
                        complete: () => {
                            this.isLoading.set(false);
                            this.requisitionForm.enable();
                        },
                    })
            );
        } else {
            this.patientExists.set(false);
            this.requisitionForm.enable();
            this.requisitionForm.reset();
            this.requisitionForm.patchValue(patchedValues)
        }
    }

    onEnterPressed(): void {
        this.fetchPatientDebounce$.next();
    }

    onOrderRequisitionClick(): void {
        const newReqFormData = this.requisitionForm.getRawValue();
        this.requisitionService.postSaveRequisition(newReqFormData).subscribe({
            next: (schemaIds: number[]) => {
                if (schemaIds.length > 0) {
                    schemaIds.forEach(id => {
                        this.schemaRepository.updateSchemaStoreByRequisition(id);
                    });
                }
            },
            error: () => {
                this.requisitionSendError.set(true);
                this.requisitionIsSent.set(false);
            },
            complete: () => {
                this.newRequisitionEvent.emit();
                this.requisitionSendError.set(false);
                this.requisitionIsSent.set(true);
            }
        });
    }

    onClose(): void {
        this.ngOnDestroy();
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        };
        this.resetForm();
        this.modalElement?.removeEventListener('hidden.bs.modal', this.onModalHidden);
    }
}
